/**
 * Fixture API responses for App Store screenshot mode. Served by the RTK
 * Query base query instead of the network when SCREENSHOT_MODE is on.
 *
 * Demo person "Alex" (example.com only). Program 421, started eight weeks
 * ago, with a realistic mix of Loss, Maintenance, and Reward days, small
 * weigh-in ups and downs, and meal notes in the style of the seed catalog.
 */
import { getExpectedDayType } from '@shared/meals';
import { addDays, differenceInCalendarDays, format, startOfWeek } from 'date-fns';
export const SCREENSHOT_USER_ID = 4242;
const PROGRAM_TYPE = '421';
const WEEK_STARTS_ON = 1;
const KCAL_GOAL = 1813;
/** Pounds to kilograms. */
const LB = 0.45359237;
/**
 * Current-week pounds, Monday through Sunday. Wednesday is the low.
 * Thursday ticks back up, so the week is a net loss with a small rebound.
 * Older weeks sit 0.6 lb higher per week.
 */
const WEEK_LB = [199.6, 199.0, 198.1, 198.6, 198.3, 198.4, 198.8];
const dayStr = (offset) => format(addDays(new Date(), offset), 'yyyy-MM-dd');
const thisMonday = () => startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON });
/** Monday eight weeks before this week, so the open week is week 9. */
const programStart = () => addDays(thisMonday(), -56);
export const SCREENSHOT_LONG_LUNCH = 'Small greek salad, 4oz boneless skinless chicken breast, 1/4 oz roasted unsalted sunflower seeds, 5oz steamed broccoli';
export const SCREENSHOT_TODAY_LUNCH = {
    entryId: 'ss-lunch',
    description: SCREENSHOT_LONG_LUNCH,
    consumedTime: '12:30',
    estimatedCalories: 480,
};
const SLOTS_FOR = {
    loss: ['breakfast', 'morning_snack', 'lunch', 'afternoon_snack'],
    maintenance: ['breakfast', 'morning_snack', 'lunch', 'dinner'],
    reward: ['breakfast', 'lunch', 'afternoon_snack', 'dinner', 'evening_snack'],
};
/** Notes and calories follow backend/scripts/mealmap/seedMealsWithValidation.ts. */
const CATALOG = {
    loss: {
        breakfast: [
            {
                description: 'Greek yogurt with honey and mixed berries',
                time: '07:40',
                kcal: 280,
            },
            {
                description: 'Scrambled egg whites with spinach and tomato',
                time: '07:55',
                kcal: 240,
            },
            {
                description: '5oz skinless chicken breast, cherry tomatoes',
                time: '08:05',
                kcal: 310,
            },
        ],
        morning_snack: [
            { description: 'A crisp apple', time: '10:15', kcal: 95 },
            { description: 'Handful of almonds', time: '10:30', kcal: 120 },
            { description: 'Large orange, 5 strawberries', time: '10:20', kcal: 110 },
        ],
        lunch: [
            {
                description: SCREENSHOT_LONG_LUNCH,
                time: '12:30',
                kcal: 480,
            },
            {
                description: 'Grilled chicken breast with steamed broccoli and quinoa',
                time: '12:40',
                kcal: 460,
            },
            {
                description: 'Large greek salad with grilled chicken',
                time: '12:25',
                kcal: 420,
            },
        ],
        afternoon_snack: [
            { description: 'Baby carrots with hummus', time: '15:30', kcal: 140 },
            { description: 'Cottage cheese with pineapple', time: '15:45', kcal: 160 },
            { description: '5 large strawberries', time: '16:00', kcal: 80 },
        ],
        dinner: [
            {
                description: '16 oz protein blueberry protein shake, 20 cooked shrimp, 1 large apple',
                time: '18:30',
                kcal: 520,
            },
            {
                description: 'Baked salmon with roasted vegetables',
                time: '18:45',
                kcal: 490,
            },
        ],
    },
    maintenance: {
        breakfast: [
            { description: 'Avocado toast with egg', time: '08:00', kcal: 380 },
            {
                description: 'Oatmeal with almond butter and fruit',
                time: '07:50',
                kcal: 360,
            },
        ],
        morning_snack: [
            { description: 'Yogurt with granola', time: '10:30', kcal: 180 },
            { description: 'Blueberries, pistachios', time: '10:15', kcal: 150 },
        ],
        lunch: [
            {
                description: 'Grilled shrimp and quinoa bowl',
                time: '12:35',
                kcal: 540,
            },
            {
                description: 'Poke bowl with salmon and avocado',
                time: '12:45',
                kcal: 560,
            },
        ],
        dinner: [
            {
                description: 'Beef stir-fry with vegetables and rice',
                time: '18:40',
                kcal: 620,
            },
            {
                description: '7 oz salmon, rice pilaf, broccoli',
                time: '19:00',
                kcal: 580,
            },
        ],
    },
    reward: {
        breakfast: [
            { description: 'Pancakes with syrup and berries', time: '09:00', kcal: 540 },
            { description: 'Croissant and latte', time: '09:15', kcal: 480 },
        ],
        lunch: [
            { description: 'Cheeseburger with fries', time: '12:50', kcal: 780 },
            {
                description: 'Pulled pork sandwich, coleslaw, 6 oz blueberries',
                time: '13:00',
                kcal: 690,
            },
        ],
        afternoon_snack: [
            { description: 'Ice cream cone', time: '15:30', kcal: 280 },
            { description: '1 slice apple pie', time: '16:00', kcal: 320 },
        ],
        dinner: [
            {
                description: 'Steak with mashed potatoes and gravy',
                time: '19:00',
                kcal: 820,
            },
            {
                description: 'Salmon sashimi, tuna sashimi, sea bass sashimi, miso soup, choc chip cookie',
                time: '19:15',
                kcal: 740,
            },
        ],
        evening_snack: [
            {
                description: 'A few squares of dark chocolate',
                time: '20:30',
                kcal: 180,
            },
        ],
    },
};
const ALL_SLOTS = [
    'breakfast',
    'morning_snack',
    'lunch',
    'afternoon_snack',
    'dinner',
    'evening_snack',
];
const dayTypeFor = (date) => getExpectedDayType(PROGRAM_TYPE, date, WEEK_STARTS_ON);
/** Day type for the screenshot "today", matching the Meal Map strip. */
export const screenshotTodayDayType = () => dayTypeFor(new Date());
const weightLb = (date) => {
    const monday = startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON });
    const weeksAgo = differenceInCalendarDays(thisMonday(), monday) / 7;
    const index = differenceInCalendarDays(date, monday);
    return Math.round((WEEK_LB[index] + weeksAgo * 0.6) * 10) / 10;
};
const kgFromLb = (lb) => Math.round(lb * LB * 100) / 100;
const mealsOn = (date) => {
    const iso = format(date, 'yyyy-MM-dd');
    const dayType = dayTypeFor(date);
    const n = date.getDate();
    return SLOTS_FOR[dayType].flatMap(slot => {
        const options = CATALOG[dayType][slot] ?? CATALOG.loss[slot] ?? [];
        const chosen = options[n % options.length];
        if (!chosen) {
            return [];
        }
        const row = {
            slot,
            description: chosen.description,
            time: chosen.time,
            kcal: chosen.kcal,
            entryId: `ss-${iso}-${slot}`,
        };
        if (iso === dayStr(0) && slot === 'lunch') {
            row.description = SCREENSHOT_TODAY_LUNCH.description;
            row.time = SCREENSHOT_TODAY_LUNCH.consumedTime;
            row.kcal = SCREENSHOT_TODAY_LUNCH.estimatedCalories;
            row.entryId = SCREENSHOT_TODAY_LUNCH.entryId;
        }
        return [row];
    });
};
const user = () => ({
    id: SCREENSHOT_USER_ID,
    signupDate: format(addDays(programStart(), -4), 'yyyy-MM-dd'),
    email: 'alex@example.com',
    firstName: 'Alex',
    lastName: 'M',
    role: { name: 'User' },
    programType: PROGRAM_TYPE,
    weekStartsOn: WEEK_STARTS_ON,
    programStartDate: format(programStart(), 'yyyy-MM-dd'),
    createdAt: format(addDays(programStart(), -4), 'yyyy-MM-dd'),
    updatedAt: dayStr(0),
    pendingDeletionRequest: null,
    programCompleted: false,
    profile: {
        birthDate: '1984-03-02',
        gender: 'M',
        phoneNumber: '',
        existingWeight: 205 * LB,
        idealWeight: 180 * LB,
        heightCm: 180,
        athleteMode: false,
        timezone: 'America/New_York',
    },
});
const done = (row) => ({
    done: true,
    entryId: row.entryId,
    description: row.description,
    consumedTime: row.time,
    estimatedCalories: row.kcal,
});
const mealStatus = (dateIso) => {
    const date = new Date(`${dateIso}T12:00:00`);
    const dayType = dayTypeFor(date);
    const rows = mealsOn(date);
    const slots = {};
    for (const slot of ALL_SLOTS) {
        slots[slot] = { done: false };
    }
    for (const row of rows) {
        slots[row.slot] = done(row);
    }
    const eaten = rows.reduce((sum, row) => sum + row.kcal, 0);
    const isToday = dateIso === dayStr(0);
    return {
        userId: SCREENSHOT_USER_ID,
        date: dateIso,
        slots,
        summary: {
            doneCount: rows.length,
            totalSlots: 6,
            doneSlots: rows.map(row => row.slot),
            requiredDoneCount: Math.min(2, rows.length),
            requiredSlots: dayType === 'loss' ? 4 : 6,
            snackDoneCount: rows.filter(row => row.slot.includes('snack')).length,
            isComplete: false,
        },
        dayType,
        defaultDayType: dayType,
        isDayTypeOverridden: false,
        mealCaloriesTotal: eaten,
        mealCaloriesPartial: false,
        mealsMissingEstimate: 0,
        restingBmr: KCAL_GOAL,
        restingBmrSource: 'scale',
        exerciseMinutes: isToday ? 35 : null,
        exerciseBurnKcal: isToday ? 210 : null,
        intakeVsRestingBmr: { delta: eaten - KCAL_GOAL, underOver: 'under' },
    };
};
const mealMap = (startIso, days) => {
    const start = new Date(`${startIso}T12:00:00`);
    const meals = {
        breakfast: {},
        morning_snack: {},
        lunch: {},
        afternoon_snack: {},
        dinner: {},
        evening_snack: {},
    };
    const measurements = {};
    const dayTypes = {};
    const calorieStatus = {};
    const today = dayStr(0);
    for (let i = 0; i < days; i++) {
        const date = addDays(start, i);
        const iso = format(date, 'yyyy-MM-dd');
        const dayType = dayTypeFor(date);
        dayTypes[iso] = {
            dayType,
            defaultDayType: dayType,
            isDayTypeOverridden: false,
        };
        if (iso > today) {
            continue;
        }
        const rows = mealsOn(date);
        for (const row of rows) {
            meals[row.slot][iso] = [
                {
                    text: row.description,
                    type: dayType,
                    expectedType: dayType,
                    offPlan: false,
                    id: row.entryId,
                    consumedTime: row.time,
                    estimatedCalories: row.kcal,
                },
            ];
        }
        measurements[iso] = { weight: kgFromLb(weightLb(date)) };
        calorieStatus[iso] = {
            mealCaloriesTotal: rows.reduce((sum, row) => sum + row.kcal, 0),
            mealCaloriesPartial: false,
        };
    }
    return {
        startDate: startIso,
        days,
        meals,
        measurements,
        dayTypes,
        calorieStatus,
    };
};
const startOfToday = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
};
const reading = (date, iso) => {
    const weeksAgo = differenceInCalendarDays(thisMonday(), startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON })) / 7;
    const isToday = iso === dayStr(0);
    return {
        date: iso,
        user: { id: SCREENSHOT_USER_ID },
        measurements: {
            weight: kgFromLb(weightLb(date)),
            bodyFat: Math.round((22.4 + weeksAgo * 0.08) * 10) / 10,
            ...(isToday
                ? {
                    water: 6.3,
                    exerciseMinutes: 35,
                    exerciseType: 'walking',
                    exerciseSessions: [{ exerciseType: 'walking', minutes: 35 }],
                }
                : {}),
        },
    };
};
/** Weight trend for Home and Weigh In, plus every day of the open week. */
const weighIns = () => {
    const today = startOfToday();
    const monday = thisMonday();
    const byDate = new Map();
    for (let ago = 42; ago >= 0; ago--) {
        const date = addDays(today, -ago);
        const iso = format(date, 'yyyy-MM-dd');
        const weekMonday = startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON });
        const inThisWeek = weekMonday.getTime() === monday.getTime();
        if (!inThisWeek && ago % 2 === 1) {
            continue;
        }
        byDate.set(iso, reading(date, iso));
    }
    return [...byDate.values()];
};
const param = (url, key) => {
    const match = url.match(new RegExp(`[?&]${key}=([^&]+)`));
    return match ? decodeURIComponent(match[1]) : undefined;
};
/**
 * Returns an RTK Query result for a request in screenshot mode. Writes
 * (POST/PATCH/DELETE) succeed with an echo and change nothing.
 */
const weeklyNetDown = () => {
    const monday = thisMonday();
    const weekStart = format(monday, 'yyyy-MM-dd');
    const weekEnd = format(addDays(monday, 6), 'yyyy-MM-dd');
    const nextWeekStart = format(addDays(monday, 7), 'yyyy-MM-dd');
    const startLb = WEEK_LB[0];
    const lowLb = Math.min(...WEEK_LB);
    const lowIndex = WEEK_LB.indexOf(lowLb);
    const dayOffset = Math.min(6, Math.max(0, differenceInCalendarDays(new Date(), monday)));
    return {
        userId: SCREENSHOT_USER_ID,
        timeZone: 'America/New_York',
        today: dayStr(0),
        weekStartsOn: WEEK_STARTS_ON,
        weekStartsOnSource: 'user',
        programType: PROGRAM_TYPE,
        programStartDate: format(programStart(), 'yyyy-MM-dd'),
        programStartSource: 'activation-note',
        idealWeightLb: 160,
        firstProgramWeekKnown: true,
        truncated: false,
        assumptionsPendingAbbi: [],
        rules: {
            source: 'defaults',
            error: null,
            goalBands: [
                { maxPoundsLeftInclusive: 30, minLb: 1, maxLb: 2 },
                { maxPoundsLeftInclusive: null, minLb: 2, maxLb: 4 },
            ],
            defaultGoalMinLb: 2,
            defaultGoalMaxLb: 4,
            overNetDownWarningLb: 4,
            exemptFirstProgramWeekFromOverWarning: true,
            monthlyBenchmarkLb: 10,
            monthlyFlagThresholdLb: 3,
            grossLossMethod: 'first-day-to-last-day',
            boundaryWeighIn: 'calendar-day-then-next-days',
            boundaryFallbackDays: 1,
            monthlyAttribution: 'next-week-start',
            programStartEvent: 'earliest-activation',
            holdingAfterRewardDay: 'not-implemented',
        },
        weeks: [
            {
                weekIndex: 9,
                isFirstProgramWeek: false,
                weekStart,
                weekEnd,
                nextWeekStart,
                startWeightLb: startLb,
                startWeightDate: weekStart,
                startWeightUsedFallback: false,
                endWeightLb: null,
                endWeightDate: null,
                endWeightUsedFallback: false,
                netChangeLb: null,
                provisionalNetChangeLb: dayOffset > 0
                    ? Number((startLb - WEEK_LB[dayOffset]).toFixed(1))
                    : null,
                provisionalEndWeightLb: dayOffset > 0 ? WEEK_LB[dayOffset] : null,
                provisionalEndWeightDate: dayOffset > 0
                    ? format(addDays(monday, dayOffset), 'yyyy-MM-dd')
                    : null,
                grossLossLb: Number((startLb - WEEK_LB[dayOffset]).toFixed(1)),
                grossStartWeightLb: startLb,
                grossStartWeightDate: weekStart,
                grossEndWeightLb: null,
                grossEndWeightDate: null,
                lowestWeightLb: lowLb,
                lowestWeightDate: format(addDays(monday, lowIndex), 'yyyy-MM-dd'),
                poundsLeftToLose: Number((startLb - 160).toFixed(1)),
                goalMinLb: 1,
                goalMaxLb: 2,
                goalBandSource: 'pounds-left',
                status: 'in-progress',
                resultLabel: 'in progress',
                checkInWarning: false,
                incomplete: true,
                incompleteReason: 'awaiting-next-week',
                rewardDays: [format(addDays(monday, 6), 'yyyy-MM-dd')],
            },
        ],
        months: [],
    };
};
export const screenshotFixtureResponse = (args) => {
    const url = args.url.replace(/^\/+/, '');
    const method = (args.method || 'GET').toUpperCase();
    const path = url.split('?')[0].replace(/\/+$/, '');
    if (method !== 'GET') {
        return { data: args.body ?? {} };
    }
    if (path === 'auth/me' || path.startsWith('users/')) {
        return { data: user() };
    }
    if (path === 'mealEntries/status') {
        return { data: mealStatus(param(url, 'date') || dayStr(0)) };
    }
    if (path === 'mealMap') {
        const start = param(url, 'startDate') || format(thisMonday(), 'yyyy-MM-dd');
        const days = Number(param(url, 'days') || '7');
        return { data: mealMap(start, Number.isFinite(days) && days > 0 ? days : 7) };
    }
    if (path === 'me/weekly-net-down') {
        return { data: weeklyNetDown() };
    }
    if (path === 'biometrics') {
        const date = param(url, 'date');
        const all = weighIns();
        return { data: { data: date ? all.filter(b => b.date === date) : all } };
    }
    if (path === 'programState') {
        const today = new Date();
        return {
            data: {
                state: 'active',
                expectedDayType: dayTypeFor(today),
                offPlanToday: false,
                lastOffPlanDate: null,
                recentOffPlanCount7: 0,
                suggestion: '',
            },
        };
    }
    if (path.startsWith('favorites')) {
        return { data: [] };
    }
    return { data: { data: [], hasNextPage: false } };
};
